import React, { Suspense, lazy } from 'react'
import { connect } from 'react-redux'
import LoadingScreen from './shared/loading_screen/LoadingScreen'
import SlimLoadingScreen from './shared/slim_loading_screen/SlimLoadingScreen'
import Header from './shared/header/Header'
import Ticker from '../../components/ticker/Ticker'
import OldBrowserNotice from '../../components/old_browser_notice/OldBrowserNotice'
import SuspenseLoader from './../../components/suspense_loader/SuspenseLoader'
import SimControlsV3 from './shared/sim_controls/SimControlsV3'
import { getLivetrackerImage } from '../../utils/js/shared_helpers'

import './Livetrackers.scss'
import useIsMobile from '../../context/IsMobileContext'

const HockeySlimLivetracker = lazy(() => import('./slim_trackers/hockey/HockeySlimLivetracker'))
const NBALivetracker = lazy(() => import('./basketball/nba/NBALivetracker'))
const NCAAMBLivetracker = lazy(() => import('./basketball/ncaamb/NCAAMBLivetracker'))
const WNBALivetracker = lazy(() => import('./basketball/wnba/WNBALivetracker'))
const OlympicsBasketballLivetracker = lazy(() =>
    import('./basketball/olympics/OlympicsLivetracker')
)
const NHLLivetracker = lazy(() => import('./nhl/NHLLivetracker'))
const MLBLivetracker = lazy(() => import('./mlb/MLBLivetracker'))
const NFLLivetracker = lazy(() => import('./football/nfl/NFLLivetracker'))
const CFLLivetracker = lazy(() => import('./football/cfl/CFLLivetracker'))
const SoccerLivetracker = lazy(() => import('./soccer/SoccerLivetracker'))
const WBCLivetracker = lazy(() => import('./wbc/WBCLivetracker'))

const Livetrackers = props => {
    const { isMobileApp } = useIsMobile()

    const { data, isFetching, fourohfour, timestamp, status, details } = props
    const { sport, gamecode } = props.match.params
    const type = details?.type || ''

    let league = props.match.params.league === 'world-juniors' ? 'wjc' : props.match.params.league

    const headerImage = getLivetrackerImage(league, sport, type)

    const isData = Object.keys(data).length !== 0

    const showSimControls =
        process.env.REACT_APP_ENV === 'integration' || process.env.REACT_APP_ENV === 'local'

    // Slim Trackers or Reg Trackers
    if (
        league === 'whc' ||
        league === 'ahl' ||
        league === 'wjc' ||
        league === 'wwhc' ||
        league === 'chl' ||
        league === 'ohl' ||
        league === 'qmjhl' ||
        league === 'whl' ||
        league === 'oly_mhk' ||
        league === 'oly_whk'
    ) {
        return (
            <React.Fragment>
                {showSimControls && (
                    <SimControlsV3 league={league} gameID={details?.id} timestamp={timestamp} />
                )}
                <main className="App">
                    {!isData && isFetching && !fourohfour && (
                        <SlimLoadingScreen mobileApp={isMobileApp} />
                    )}
                    <Suspense fallback={<SuspenseLoader />}>
                        <HockeySlimLivetracker gamecode={gamecode} sport={sport} league={league} />
                    </Suspense>
                </main>
                <OldBrowserNotice />
            </React.Fragment>
        )
    } else {
        const tickerLeagues = [] // disable mobile ticker for all leagues (temporarily)
        const tickerSports = [] // disable mobile ticker for all sports (temporarily)

        return (
            <React.Fragment>
                {showSimControls && ( // SR_MIGRATION
                    <SimControlsV3 league={league} gameID={details?.id} timestamp={timestamp} />
                )}

                <Header
                    headerImage={league !== 'ncaamb' ? headerImage : null}
                    isMobileApp={isMobileApp}
                    league={league}
                    status={status}
                    details={details}
                    type={type}
                />

                <main className="App">
                    {isData && (tickerLeagues.includes(league) || tickerSports.includes(sport)) && (
                        <Ticker
                            league={league}
                            sport={sport}
                            multiLeague={tickerSports.includes(sport)}
                        />
                    )}
                    {!isData && isFetching && !fourohfour && (
                        <LoadingScreen mobileApp={isMobileApp} />
                    )}
                    <Suspense fallback={<SuspenseLoader />}>
                        {league === 'nhl' && <NHLLivetracker {...props} />}
                        {league === 'mlb' && <MLBLivetracker {...props} />}
                        {league === 'wbc' && <WBCLivetracker {...props} />}
                        {league === 'nba' && <NBALivetracker {...props} />}
                        {league === 'ncaamb' && <NCAAMBLivetracker {...props} />}
                        {league === 'wnba' && <WNBALivetracker {...props} />}
                        {(league === 'oly_wbk' || league === 'oly_mbk') && (
                            <OlympicsBasketballLivetracker {...props} />
                        )}
                        {league === 'nfl' && <NFLLivetracker {...props} />}
                        {league === 'cfl' && <CFLLivetracker {...props} />}
                        {(league === 'mls' ||
                            league === 'premier-league' ||
                            league === 'ligue-1' ||
                            league === 'la-liga' ||
                            league === 'champions-league' ||
                            league === 'bundesliga' ||
                            league === 'serie-a' ||
                            league === 'euro' ||
                            league === 'world-cup-2022' ||
                            league === 'natl' ||
                            league === 'oly_msoc' ||
                            league === 'oly_wsoc' ||
                            league === 'eng_fa_cup' ||
                            league === 'wwc' ||
                            league === 'womens-world-cup') && <SoccerLivetracker {...props} />}
                    </Suspense>
                </main>
                <OldBrowserNotice />
            </React.Fragment>
        )
    }
}

const mapStateToProps = state => {
    return {
        status: state.data?.details?.status,
        data: state.data,
        details: state.data.details,
        timestamp: state.data?.details?.timestamp,
        isFetching: state.isFetching,
        fourohfour: state.fourohfour,
    }
}

export default connect(mapStateToProps)(Livetrackers)
